/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useCallback } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { Icons } from '@web-stories-wp/design-system';
import { STORY_ANIMATION_STATE } from '@web-stories-wp/animation';
import styled, { css } from 'styled-components';

/**
 * Internal dependencies
 */
import StoryPropTypes from '../../types';
import { useStory } from '../../app';

const PLAY_BUTTON_SIZE = 82;
const ICON_SVG_SIZE = 72;

const Controls = styled.div`
  position: absolute;
  z-index: 2;
  left: ${({ x }) => `${x}px`};
  top: ${({ y }) => `${y}px`};
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
  pointer-events: none;
`;

const ButtonWrapper = styled.div.attrs({ role: 'button', tabIndex: -1 })`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  pointer-events: initial;
  width: ${PLAY_BUTTON_SIZE}px;
  height: ${PLAY_BUTTON_SIZE}px;
  overflow: hidden;
`;

const iconCss = css`
  width: ${ICON_SVG_SIZE}px;
  height: ${ICON_SVG_SIZE}px;
  pointer-events: none;
  transform: translate(
    ${(PLAY_BUTTON_SIZE - ICON_SVG_SIZE) / 2}px,
    ${(PLAY_BUTTON_SIZE - ICON_SVG_SIZE) / 2}px
  );
  color: ${({ theme }) => theme.colors.standard.white};
  filter: drop-shadow(
    0px 0px 10px ${({ theme }) => theme.colors.opacity.black64}
  );
`;

const Play = styled(Icons.PlayFilled)`
  ${iconCss};
`;

const Stop = styled(Icons.StopFilled)`
  ${iconCss};
`;

function LottieControls({
  box,
  isSelected,
  isSingleElement,
  isEditing,
  isTransforming,
  element,
}) {
  const { animationState, updateAnimationState } = useStory(
    ({ state: { animationState }, actions: { updateAnimationState } }) => ({
      animationState,
      updateAnimationState,
    })
  );
  const isActive =
    isSelected && !isTransforming && isSingleElement && !isEditing;
  const isPlaying = [
    STORY_ANIMATION_STATE.PLAYING,
    STORY_ANIMATION_STATE.PLAYING_SELECTED,
  ].includes(animationState);

  const handlePlayPause = useCallback(
    (evt) => {
      evt.stopPropagation();

      updateAnimationState({
        animationState: isPlaying
          ? STORY_ANIMATION_STATE.RESET
          : STORY_ANIMATION_STATE.PLAYING_SELECTED,
      });
    },
    [isPlaying, updateAnimationState]
  );

  if (!isActive) {
    return null;
  }

  const Icon = isPlaying ? Stop : Play;
  const buttonLabel = isPlaying
    ? __('Click to stop', 'web-stories')
    : __('Click to play', 'web-stories');

  return (
    <Controls data-controls-id={element.id} {...box}>
      <ButtonWrapper
        aria-label={buttonLabel}
        aria-pressed={isPlaying}
        onMouseDown={handlePlayPause}
      >
        <Icon />
      </ButtonWrapper>
    </Controls>
  );
}

LottieControls.propTypes = {
  box: StoryPropTypes.box.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isSingleElement: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool.isRequired,
  isTransforming: PropTypes.bool.isRequired,
  element: StoryPropTypes.elements.lottie.isRequired,
};

export default LottieControls;
